"use client";

import { Award, CheckCircle, FileText, Shield, User } from "lucide-react";
import type { ComponentType } from "react";

import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ActionRequirement {
  icon: ComponentType<{ className?: string }>;
  text: string;
}

export enum ActionType {
  ExpressInterest = "express_interest",
}

export type RequirementKey =
  | "ProfileInfo"
  | "Resume"
  | "WorkEligibility"
  | "Credential"
  | "Verification";

interface ActionRequirementsModalContentViewProps {
  schoolName: string;
  actionType: ActionType;
  onGetStarted: () => void;
  onMaybeLater: () => void;
}

const REQUIREMENTS: Record<RequirementKey, ActionRequirement> = {
  ProfileInfo: {
    icon: User,
    text: "Complete your profile information",
  },
  Resume: {
    icon: FileText,
    text: "Upload your resume",
  },
  Credential: {
    icon: Award,
    text: "Add at least one teaching credential",
  },
  WorkEligibility: {
    icon: Shield,
    text: "Confirm US work eligibility",
  },
  Verification: {
    icon: CheckCircle,
    text: "Profile Verification",
  },
};

const ACTION_REQUIREMENTS: Record<ActionType, RequirementKey[]> = {
  [ActionType.ExpressInterest]: [
    "ProfileInfo",
    "Resume",
    "Credential",
    "WorkEligibility",
    "Verification",
  ],
};

const getRequirements = (actionType: ActionType): ActionRequirement[] => {
  const requirementKeys = ACTION_REQUIREMENTS[actionType];
  return requirementKeys.map((key) => REQUIREMENTS[key]);
};

const getActionCopy = (schoolName: string) => ({
  title: `Express Interest to ${schoolName}`,
  description:
    "Express Interest lets you connect directly with principals before submitting a formal application. It's a great way to start a conversation!",
  requirementsHeading: "To express interest, you'll need to:",
});

export default function ActionRequirementsModalContentView({
  schoolName,
  actionType,
  onGetStarted,
  onMaybeLater,
}: ActionRequirementsModalContentViewProps) {
  const copy = getActionCopy(schoolName);
  const requirements = getRequirements(actionType);

  return (
    <div className="px-5 py-5 sm:px-6 sm:py-6 space-y-4">
      <DialogHeader className="text-left space-y-4">
        <DialogTitle className="text-center text-xl sm:text-2xl font-bold text-foreground leading-tight">
          {copy.title}
        </DialogTitle>
        <DialogDescription className="text-left text-base text-ink-emphasis">
          {copy.description}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <div className="bg-surface-subtle rounded-lg border border-border p-4 space-y-3">
            <h3 className="text-lg font-semibold text-foreground mb-3">
              {copy.requirementsHeading}
            </h3>
            {requirements.map((requirement, index) => {
              const Icon = requirement.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 text-base text-ink-emphasis"
                >
                  <Icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span>{requirement.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-center text-muted-foreground py-2">
          Don&apos;t worry — we&apos;ll guide you through each step!
        </p>
      </div>

      <DialogFooter className="flex-col gap-2 sm:flex-col">
        <Button
          onClick={onGetStarted}
          className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-lg py-3 text-md"
          variant="default"
        >
          Get Started
        </Button>
        <Button
          onClick={onMaybeLater}
          className="w-full h-11 text-foreground/50 hover:text-foreground font-medium text-md"
          variant="ghost"
        >
          Maybe Later
        </Button>
      </DialogFooter>
    </div>
  );
}
